import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import MetaData from '../Layouts/MetaData';
import Loader from '../Layouts/Loader';

const stripePromise = loadStripe('pk_live_xWzWSyu3bi0iBdbBeqftvUdn');

const elementStyle = {
    style: {
        base: {
            fontSize: '15px',
            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
            color: '#1a1a1a',
            '::placeholder': { color: '#9ca3af' },
        },
        invalid: { color: '#d33' },
    },
};

const LOCKED_STATUSES = ['Packed', 'Shipped', 'Out for Delivery', 'Delivered', 'Cancelled'];

const EditOrderInner = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const stripe = useStripe();
    const elements = useElements();

    const [order, setOrder] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [productSearch, setProductSearch] = useState("");
    const [productOptions, setProductOptions] = useState([]);

    const [topup, setTopup] = useState(null); // { clientSecret, difference, newTotalPrice }

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const { data } = await axios.get(`/api/v1/order/${id}`);
                setOrder(data.order);
                setItems(data.order.orderItems.map((i) => ({
                    product: i.product,
                    name: i.name,
                    price: i.price,
                    image: i.image,
                    quantity: i.quantity,
                })));
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load order", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!productSearch) { setProductOptions([]); return; }
        const timeout = setTimeout(async () => {
            try {
                const { data } = await axios.get('/api/v1/products', { params: { keyword: productSearch } });
                setProductOptions(data.products || []);
            } catch (error) { /* ignore */ }
        }, 400);
        return () => clearTimeout(timeout);
    }, [productSearch]);

    const updateQty = (product, delta) => {
        setItems((prev) => prev.map((i) => i.product === product ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i).filter((i) => i.quantity > 0));
    };

    const addProduct = (product) => {
        if (!product) return;
        setItems((prev) => {
            const existing = prev.find((i) => i.product === product._id);
            if (existing) {
                return prev.map((i) => i.product === product._id ? { ...i, quantity: i.quantity + 1 } : i);
            }
            return [...prev, { product: product._id, name: product.name, price: product.price, image: product.images?.[0]?.url, quantity: 1 }];
        });
    };

    const estimatedTotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0) + (order?.shippingCharge || 0);

    const submitEdit = async (paymentInfo) => {
        const payload = {
            orderItems: items.map((i) => ({ product: i.product, quantity: i.quantity })),
        };
        if (paymentInfo) payload.paymentInfo = paymentInfo;

        const { data } = await axios.put(`/api/v1/order/${id}/edit`, payload);
        enqueueSnackbar("Order updated successfully", { variant: "success" });
        navigate(`/order_details/${data.order._id}`);
    };

    const saveHandler = async () => {
        if (items.length === 0) {
            enqueueSnackbar("Your order must have at least one item", { variant: "error" });
            return;
        }

        setSaving(true);
        try {
            const needsPayment = order.paymentMethod !== 'cod' && estimatedTotal > order.totalPrice;

            if (needsPayment && !topup) {
                const { data } = await axios.post(`/api/v1/order/${id}/edit-topup-intent`, {
                    orderItems: items.map((i) => ({ product: i.product, quantity: i.quantity })),
                });
                setTopup(data);
                setSaving(false);
                return; // show the card form now, customer confirms in a second step
            }

            if (topup) {
                const { error, paymentIntent } = await stripe.confirmCardPayment(topup.clientSecret, {
                    payment_method: { card: elements.getElement(CardNumberElement) },
                });
                if (error) throw new Error(error.message);
                await submitEdit({ id: paymentIntent.id, status: paymentIntent.status });
            } else {
                await submitEdit(null);
            }
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || error.message || "Failed to save changes", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Loader />;
    if (!order) return null;

    if (LOCKED_STATUSES.includes(order.orderStatus)) {
        return (
            <main className="w-full mt-20">
                <div className="flex flex-col gap-2 items-center justify-center sm:w-4/6 sm:mt-4 m-auto mb-7 bg-white shadow rounded p-6 pb-12">
                    <h1 className="text-xl font-semibold text-red-600">This order can no longer be edited</h1>
                    <p className="text-gray-600 mt-2">It has already been packed and is being prepared for delivery.</p>
                    <Link to={`/order_details/${id}`} className="bg-primary-blue mt-4 py-2.5 px-6 text-white uppercase shadow hover:shadow-lg rounded-sm">Back to Order</Link>
                </div>
            </main>
        );
    }

    return (
        <main className="w-full mt-14 sm:mt-4">
            <MetaData title="Edit Order | 1313 Mart" />
            <div className="flex flex-col gap-4 max-w-4xl mx-auto">

                <h1 className="text-xl font-semibold">Edit Order #{order.orderNumber ?? order._id}</h1>

                <div className="bg-white shadow rounded-sm p-4 flex flex-col gap-3">
                    {items.map((item) => (
                        <div key={item.product} className="flex items-center gap-3 border-b last:border-0 pb-3 last:pb-0">
                            <img draggable="false" className="w-12 h-12 object-contain flex-shrink-0" src={item.image} alt={item.name} />
                            <span className="flex-1 text-sm">{item.name}</span>
                            <span className="text-sm text-gray-500">HK${item.price.toLocaleString()}</span>
                            <div className="flex items-center gap-1">
                                <button onClick={() => updateQty(item.product, -1)} className="w-7 h-7 bg-gray-50 rounded-full border flex items-center justify-center"><RemoveIcon sx={{ fontSize: "16px" }} /></button>
                                <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                                <button onClick={() => updateQty(item.product, 1)} className="w-7 h-7 bg-gray-50 rounded-full border flex items-center justify-center"><AddIcon sx={{ fontSize: "16px" }} /></button>
                            </div>
                            <span className="text-sm font-medium w-20 text-right">HK${(item.price * item.quantity).toLocaleString()}</span>
                            <button onClick={() => updateQty(item.product, -item.quantity)} className="text-red-500"><DeleteIcon sx={{ fontSize: "18px" }} /></button>
                        </div>
                    ))}

                    <Autocomplete
                        size="small"
                        options={productOptions}
                        getOptionLabel={(opt) => opt.name || ""}
                        onChange={(e, newValue) => addProduct(newValue)}
                        onInputChange={(e, newInput) => setProductSearch(newInput)}
                        value={null}
                        renderInput={(params) => <TextField {...params} label="Add another product" placeholder="Search products..." />}
                    />
                </div>

                <div className="bg-white shadow rounded-sm p-4 flex flex-col gap-1 items-end text-sm">
                    <p className="flex justify-between w-full sm:w-64"><span>Items Total</span> <span>HK${(estimatedTotal - (order.shippingCharge || 0)).toLocaleString()}</span></p>
                    <p className="flex justify-between w-full sm:w-64"><span>Shipping</span> <span>{order.shippingCharge > 0 ? `HK$${order.shippingCharge.toLocaleString()}` : "FREE"}</span></p>
                    <p className="flex justify-between w-full sm:w-64 font-medium text-base border-t pt-1 mt-1"><span>Estimated Total</span> <span>HK${estimatedTotal.toLocaleString()}</span></p>
                    <p className="text-xs text-gray-400 mt-1">Original total was HK${order.totalPrice.toLocaleString()} — final shipping and total are confirmed by the server on save.</p>
                </div>

                {topup && (
                    <div className="bg-white shadow rounded-sm p-5 flex flex-col gap-3">
                        <p className="text-sm font-medium text-gray-700">Additional payment needed: <span className="text-primary-orange">HK${topup.difference.toLocaleString()}</span></p>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Card Number</label>
                            <div className="border border-gray-300 rounded-md px-3 py-2.5"><CardNumberElement options={{ ...elementStyle, placeholder: '1234 1234 1234 1234' }} /></div>
                        </div>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Expiry Date</label>
                                <div className="border border-gray-300 rounded-md px-3 py-2.5"><CardExpiryElement options={elementStyle} /></div>
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-500 mb-1">CVV</label>
                                <div className="border border-gray-300 rounded-md px-3 py-2.5"><CardCvcElement options={elementStyle} /></div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-3 mb-8">
                    <Link to={`/order_details/${id}`} className="py-2.5 px-6 text-gray-600 uppercase text-sm font-medium">Cancel</Link>
                    <button
                        onClick={saveHandler}
                        disabled={saving}
                        className="bg-primary-orange py-2.5 px-8 text-white uppercase text-sm font-medium shadow hover:shadow-lg rounded-sm disabled:opacity-60"
                    >
                        {saving ? "Saving..." : topup ? `Pay HK$${topup.difference.toLocaleString()} & Save` : "Save Changes"}
                    </button>
                </div>
            </div>
        </main>
    );
};

const EditOrder = () => (
    <Elements stripe={stripePromise}>
        <EditOrderInner />
    </Elements>
);

export default EditOrder;